// Page: TagCounts
// Purpose: Admin analytics page showing tag counts and user search activity
// across templates, video templates, and stock videos. Supports refresh and
// displays categorized tables with usage bars.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import Footer from '../../../components/shared/Footer';
import { ArrowLeft, BarChart3, Hash, FileText, Calendar, RefreshCw, MousePointer, Play, Camera, Image } from 'lucide-react';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { getCategorizedTagStats } from '../../../api/tags/tagsApi';
import { isUserAdmin } from '../../../utils/admin/adminUtils';

export default function TagCounts() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Categorized tag data
  const [categorizedData, setCategorizedData] = useState({
    categorizedStats: {
      template: [],
      video_template: [],
      stock_video: []
    },
    categoryTotals: {
      template: { totalTags: 0, tagsWithClicks: 0, totalClicks: 0 },
      video_template: { totalTags: 0, tagsWithClicks: 0, totalClicks: 0 },
      stock_video: { totalTags: 0, tagsWithClicks: 0, totalClicks: 0 }
    },
    overallTotals: { totalTags: 0, tagsWithClicks: 0, totalClicks: 0 },
    timestamp: null
  });
  
  // Get the auth context
  const { user } = useAuth();
  const { addToast } = useToast();
  
  // Check if user is the admin
  const isAdmin = isUserAdmin(user);

  // Delay showing errors to prevent flashing
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setShowError(true);
      }, 500); 
      return () => clearTimeout(timer);
    } else {
      setShowError(false);
    }
  }, [error]);

  // Check if user is an admin, if not, redirect to home page
  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      } else {
        // Load categorized tag data
        loadCategorizedTagData();
      }
    } else if (user === null) {
      // No user, redirect to login
      navigate('/login');
    }
  }, [navigate, user, isAdmin]);

  // Load categorized tag data function
  const loadCategorizedTagData = async (showRefreshing = false) => {
    try {
      if (showRefreshing) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      
      const response = await getCategorizedTagStats();
      setCategorizedData(response);
      setError(null);
      
      if (showRefreshing) {
        addToast('Tag statistics refreshed successfully!', 'success');
      }
    } catch (err) {
      console.error('Error loading categorized tag data:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    loadCategorizedTagData(true);
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp).toLocaleString();
  };

  // Tag category section component
  const TagCategorySection = ({ title, icon: Icon, categoryKey, tags, totals, iconColor, bgColor }) => {
    if (tags.length === 0) {
      return (
        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Icon className={`h-6 w-6 ${iconColor} mr-2`} />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h2>
          </div>
          <div className="text-center py-8">
            <Hash className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Tags Found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              No tags available for this category yet.
            </p>
          </div>
        </div>
      );
    }

    const maxCount = Math.max(...tags.map(t => t.count || 0)) || 1;
    const maxClicks = Math.max(...tags.map(t => t.clickCount || 0)) || 1;

    return (
      <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        {/* Category Header */}
        <div className={`${bgColor} px-6 py-4 border-b border-gray-200 dark:border-gray-700`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Icon className={`h-6 w-6 ${iconColor} mr-2`} />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h2>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {totals.totalTags} tags
            </div>
          </div>
          
          {/* Category Summary Stats */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{totals.totalTags}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Tags</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{totals.tagsWithClicks}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Tags with Clicks</p>
            </div>
          </div>
        </div>

        {/* Tags Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tag Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Items
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User Searches
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Usage Bars
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {tags.map((tag, index) => {
                const itemCount = tag.count || 0;
                const clickCount = tag.clickCount || 0;
                const itemBarWidth = (itemCount / maxCount) * 100;
                const clickBarWidth = (clickCount / maxClicks) * 100;
                
                return (
                  <tr key={tag.name} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      #{index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-lg text-sm font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400">
                        {tag.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {itemCount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {clickCount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap w-48">
                      <div className="space-y-2">
                        <div>
                          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                            <span>Items</span>
                            <span>{itemCount}</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div 
                              className="bg-blue-500 dark:bg-blue-400 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${itemBarWidth}%` }}
                            ></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                            <span>Searches</span>
                            <span>{clickCount}</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div 
                              className="bg-purple-500 dark:bg-purple-400 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${clickBarWidth}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (isLoading && categorizedData.overallTotals.totalTags === 0) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading tag statistics..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Redirect happens in useEffect
  }

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto pb-20">
      {/* Back to Admin Dashboard button */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/admin')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Admin
        </button>
      </div>

      {error && showError && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {error}
        </div>
      )}

      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <BarChart3 className="h-6 w-6 text-primary-600 dark:text-primary-400 mr-2" />
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Categorized Tag Analytics</h1>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-900 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-800"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          View template counts and user search activity organized by content type.
          Search data updates in real-time as users interact with your tags.
        </p>
      </div>

      {/* Overall Summary Stats */}
      <div className="mb-8 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Hash className="h-5 w-5 text-blue-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Tags</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{categorizedData.overallTotals.totalTags}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <FileText className="h-5 w-5 text-green-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Tags with Activity</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{categorizedData.overallTotals.tagsWithClicks}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <MousePointer className="h-5 w-5 text-purple-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Searches</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{categorizedData.overallTotals.totalClicks.toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Calendar className="h-5 w-5 text-orange-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Last Updated</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{formatTimestamp(categorizedData.timestamp)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Categorized Tag Sections */}
      <div className="space-y-8">
        {/* Templates Section */}
        <TagCategorySection
          title="Template Tags"
          icon={Image}
          categoryKey="template"
          tags={categorizedData.categorizedStats.template || []}
          totals={categorizedData.categoryTotals.template || {}}
          iconColor="text-blue-600"
          bgColor="bg-blue-50 dark:bg-blue-900/20"
        />

        {/* Video Templates Section */}
        <TagCategorySection
          title="Video Template Tags"
          icon={Play}
          categoryKey="video_template"
          tags={categorizedData.categorizedStats.video_template || []}
          totals={categorizedData.categoryTotals.video_template || {}}
          iconColor="text-green-600"
          bgColor="bg-green-50 dark:bg-green-900/20"
        />

        {/* Stock Videos Section */}
        <TagCategorySection
          title="Stock Video Tags"
          icon={Camera}
          categoryKey="stock_video"
          tags={categorizedData.categorizedStats.stock_video || []}
          totals={categorizedData.categoryTotals.stock_video || {}}
          iconColor="text-purple-600"
          bgColor="bg-purple-50 dark:bg-purple-900/20"
        />
      </div>

      <Footer />
    </div>
  );
} 